"use client";

import { useRef } from "react";
import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Toaster } from "@/components/ui/sonner";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { toast } from "sonner";
function ReservationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    if (typeof window !== "undefined") {
      setTimeout(() => {
        toast.success(successMessage, {
          description: "We will be in touch within a day to schedule your consultation."
        });
      }, 0);
    }
    return <div className="rounded-lg border border-border bg-card p-8">
        <p className="font-default text-2xl text-foreground">
          Request received.
        </p>
        <p className="mt-3 text-base text-muted-foreground">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-lg">
          Request another quote
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-7">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base text-foreground">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="John Smith" className="h-11 border-0 border-b border-border bg-transparent px-0 focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground">
              The name for your project file.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base text-foreground">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className="h-11 border-0 border-b border-border bg-transparent px-0 focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base text-foreground">
              Phone Number{" "}
              <span className="font-default italic text-muted-foreground text-sm">
                (for site visit coordination)
              </span>
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 555-000-0000" className="h-11 border-0 border-b border-border bg-transparent px-0 focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_type" rules={{
      required: "Please select a project type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base text-foreground">
              Project Type
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <SelectTrigger className="h-11 w-full rounded-lg border border-border bg-transparent px-4 focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary/30">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="Interior">Interior Painting</SelectItem>
                  <SelectItem value="Exterior">Exterior Painting</SelectItem>
                  <SelectItem value="Full">Full Residential</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="timeline" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base text-foreground">
              Desired Timeline
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value || ""} className="flex flex-wrap gap-3">
                {["ASAP", "Next Month", "Flexible"].map((time, i) => <label key={time} data-index={i} htmlFor={`time-${time}`} className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-muted/20 px-4 py-2 text-sm text-foreground transition-colors hover:border-primary has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/10">
                    <RadioGroupItem value={time} id={`time-${time}`} className="sr-only" />
                    {time}
                  </label>)}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base text-foreground">
              Project Details
            </FormLabel>
            <FormControl>
              <Textarea placeholder="e.g., square footage, number of rooms, or specific color preferences" className="min-h-24 rounded-lg border border-border bg-transparent focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary/30" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="newsletter_optin" render={({
      field
    }) => <FormItem className="flex flex-row items-start gap-3 space-y-0 pt-2">
            <FormControl>
              <Checkbox checked={!!field.value} onCheckedChange={field.onChange} className="mt-0.5" />
            </FormControl>
            <FormLabel className="font-default text-sm text-muted-foreground leading-relaxed">
              Keep me updated with seasonal painting tips and project inspiration.
            </FormLabel>
          </FormItem>} />

      {submitError && <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-12 w-full rounded-lg bg-primary text-primary-foreground text-base font-medium tracking-wide shadow-md transition-all hover:bg-primary/90">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending...
          </> : "Request Free Quote"}
      </Button>
    </Form>;
}
export default function StartYourTransformation() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const formCard = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const footer = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4430" id="start-your-transformation" className="relative overflow-hidden bg-background py-24 md:py-32">
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -right-32 size-[600px] rounded-lg opacity-40 blur-3xl" style={{
      background: "radial-gradient(circle at center, var(--primary), transparent 70%)"
    }} />
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -left-40 size-[480px] rounded-lg opacity-25 blur-3xl" style={{
      background: "radial-gradient(circle at center, var(--secondary), transparent 70%)"
    }} />

      <div className="relative mx-auto w-full max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10 lg:gap-16">
          <div ref={intro.ref} className={`md:col-span-5 lg:col-span-4 md:pt-12 transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-mono text-xs font-medium tracking-[0.25em] uppercase text-primary">
              Get a Quote
            </p>
            <h2 className="font-default text-foreground mt-6 text-3xl leading-[1.15] md:text-4xl lg:text-5xl">
              Ready to refresh
              <br />
              your space?
            </h2>
            <p className="mt-8 max-w-sm text-base leading-relaxed text-muted-foreground">
              Elite Brushstrokes provides premium residential painting services. Submit your details below and we will provide a custom estimate for your project.
            </p>

            <div className="mt-10 hidden h-px w-24 bg-border md:block" />
          </div>

          <div ref={formCard.ref} className={`md:col-span-7 lg:col-span-7 lg:col-start-6 transition-all duration-1000 delay-100 ease-out ${formCard.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="rounded-lg border border-border bg-card/60 p-8 shadow-lg backdrop-blur-sm md:p-12">
              <FormProvider formKey="quote_form_get_a_quote" formId="quote_form_get_a_quote" sectionName="start-your-transformation" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              project_type: "",
              timeline: "",
              details: "",
              newsletter_optin: false
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              project_type: {
                required: true
              }
            }} submitText="Request Free Quote" successMessage="Your request has been received. We will contact you shortly." errorMessage="We could not process your request. Please try again.">
                <ReservationForm />
              </FormProvider>
            </div>
          </div>
        </div>

        <div ref={footer.ref} className={`mt-20 text-center transition-all duration-1000 ease-out ${footer.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="text-base text-muted-foreground">
            Questions? Email us at{" "}
            <span className="text-foreground font-mono text-sm">
              hello@elitebrushstrokes.com
            </span>{" "}
            — we respond within 24 hours.
          </p>
        </div>
      </div>

      <Toaster position="bottom-right" />
    </section>;
}